import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, FILE_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Gift,
  Link as LinkIcon,
  Lightbulb,
  Plus,
  Trash2,
  Share2,
  Copy,
  Check,
  Info,
  Camera,
  RotateCcw,
  CalendarClock,
  Package,
  Repeat,
  Ticket,
  Wrench,
  Palette,
  HeartHandshake,
  Tag,
  Pencil,
  X as XIcon,
  ImagePlus,
  RefreshCcw,
  ThumbsUp,
  ThumbsDown,
  Sprout,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { LinkPreviewThumb } from "@/components/LinkPreviewThumb";

// Gift-item categories. `product` is the default so every legacy item stays
// happy. The labels + hints are surfaced in the UI; the ids are what get
// sent over the wire (matches the enum enforced server-side).
const ITEM_CATEGORIES = [
  {
    id: "product",
    label: "Product",
    icon: Package,
    hint: "A physical thing you'd buy — mug, jacket, cookbook.",
    specific_ph: "e.g., 'Le Creuset dutch oven, 5.5 qt cerise'",
    idea_ph: "e.g., 'Something small from a local pottery shop'",
    link_ph: "Paste the product link (or affiliate link)",
    store_label: "Store or brand",
  },
  {
    id: "subscription",
    label: "Subscription",
    icon: Repeat,
    hint: "Meal kits, book/coffee clubs, streaming — anything recurring.",
    specific_ph: "e.g., 'Trade Coffee — 6 months' or 'HelloFresh 3-meal weekly'",
    idea_ph: "e.g., 'Any indie book-of-the-month club'",
    link_ph: "Signup or gift-subscription link",
    store_label: "Service name",
  },
  {
    id: "experience",
    label: "Experience",
    icon: Ticket,
    hint: "Concerts, zoo/aquarium passes, cooking or pottery classes.",
    specific_ph: "e.g., 'Ryman: The Highwomen Sept 12' or 'Nashville Pottery Class'",
    idea_ph: "e.g., 'Any cooking class in Nashville'",
    link_ph: "Ticket page or class signup link",
    store_label: "Venue or host",
  },
  {
    id: "service",
    label: "Service",
    icon: Wrench,
    hint: "House cleaning, massage, lawn care — a gift certificate for time.",
    specific_ph: "e.g., '3-hour house cleaning' or '60-min deep-tissue massage'",
    idea_ph: "e.g., 'Someone local for one-time deep clean'",
    link_ph: "Provider's site or gift-cert link",
    store_label: "Provider",
  },
  {
    id: "handmade",
    label: "Handmade",
    icon: Palette,
    hint: "Custom sign, monogrammed thing, painted portrait — one of one.",
    specific_ph: "e.g., 'Custom family sign from Wood & Grain co.'",
    idea_ph: "e.g., 'A painted portrait of the dog'",
    link_ph: "Artist's site or Etsy shop link",
    store_label: "Artist or shop",
  },
  {
    id: "donation",
    label: "Donation",
    icon: HeartHandshake,
    hint: "Give in my name — a charity or cause I care about.",
    specific_ph: "e.g., 'World Central Kitchen — $50' or 'Nashville Rescue Mission'",
    idea_ph: "e.g., 'Any local no-kill shelter'",
    link_ph: "Donation page link",
    store_label: "Charity or cause",
  },
];
const ITEM_CATEGORY_BY_ID = Object.fromEntries(ITEM_CATEGORIES.map((c) => [c.id, c]));

// Merges preset categories with the list's owner-defined custom ones.
// Custom entries get a neutral Tag icon and a soft hint since we don't ask
// the user to describe them. Order: presets first, custom appended (matches
// the visual order in every picker on the page).
function buildAllCategories(customCategories = []) {
  const custom = (customCategories || []).map((c) => ({
    id: c.slug,
    label: c.label,
    icon: Tag,
    hint: "Your custom category on this list.",
    // Custom entries reuse the "product" placeholders since we don't know
    // the shape — feels natural for most homemade categories.
    specific_ph: "Add a specific pick for this category",
    idea_ph: "Describe what you'd love (any brand or version)",
    link_ph: "Paste a link (optional)",
    store_label: "Store or source",
    is_custom: true,
  }));
  return [...ITEM_CATEGORIES, ...custom];
}

const EMPTY_ITEM = {
  kind: "specific",
  item_category: "product",
  title: "",
  notes: "",
  price_hint: "",
  link: "",
  store_name: "",
};

export default function GiftListDetail() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [occasionEdit, setOccasionEdit] = useState("");
  const [occasionRecurringEdit, setOccasionRecurringEdit] = useState(false);
  const [occasionBusy, setOccasionBusy] = useState(false);
  // Inline editor for the gift list's own name. Opens a small input in
  // place of the <h1>; Save PATCHes the name and updates local state so
  // the page never reloads (mobile users complained the whole screen
  // blanked to "Loading…" on every edit).
  const [editingListName, setEditingListName] = useState(false);
  const [listNameDraft, setListNameDraft] = useState("");
  const [listNameSaving, setListNameSaving] = useState(false);
  // Custom (list-scoped) categories the owner has added. Kept in sync with
  // list.custom_categories from the server. The "+ Add category" pill flips
  // `newCatOpen` into an inline input; enter or Save commits.
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newCatSaving, setNewCatSaving] = useState(false);
  const customCategories = list?.custom_categories || [];
  const allCategories = buildAllCategories(customCategories);
  const allCategoriesById = Object.fromEntries(allCategories.map((c) => [c.id, c]));
  // "all" | category id. Local-only filter — the item list stays complete;
  // the filter just hides rows client-side. Cheaper than paginating.
  const [filter, setFilter] = useState("all");
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_ITEM);
  const [editSaving, setEditSaving] = useState(false);
  // Per-item preview controls (retry og:image scrape + upload your own photo).
  // We share a single file input across items and remember which item id we're
  // uploading for so a burst of clicks on different rows can't cross-wire.
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const [refreshBusyId, setRefreshBusyId] = useState(null);
  const [pendingPhotoItemId, setPendingPhotoItemId] = useState(null);
  const itemPhotoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gift-lists/${id}`);
      setList(data.list);
      setItems(data.items || []);
      setOccasionEdit(data.list?.occasion_date || "");
      setOccasionRecurringEdit(!!data.list?.occasion_recurring);
    } catch {
      toast("Couldn't load this list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, id]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...draft,
        item_category: draft.item_category || "product",
        link: draft.kind === "specific" ? (draft.link.trim() || null) : null,
      };
      const { data: added } = await api.post(`/gift-lists/${id}/items`, payload);
      // Append to local state instead of refetching the whole page — the
      // server returns the newly-created item shape, so we can just splice
      // it in place and skip the "reload" flash that mobile users hate.
      setItems((prev) => [...prev, added]);
      // Keep the same kind + category so a burst of "add 6 subscription items"
      // doesn't require re-picking the category every time.
      setDraft({ ...EMPTY_ITEM, kind: draft.kind, item_category: draft.item_category });
      toast("Added.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't add the item.");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Remove this from your list?")) return;
    // Optimistic remove — snap it off immediately, restore on error so the
    // row doesn't hang around waiting for the network round-trip.
    const snapshot = items;
    setItems((prev) => prev.filter((i) => i.id !== itemId));
    try {
      await api.delete(`/gift-items/${itemId}`);
    } catch {
      setItems(snapshot);
      toast("Couldn't remove the item.");
    }
  };

  const startEditingItem = (it) => {
    setEditDraft({
      kind: it.kind,
      item_category: it.item_category || "product",
      title: it.title || "",
      notes: it.notes || "",
      price_hint: it.price_hint || "",
      link: it.link || "",
      store_name: it.store_name || "",
    });
    setEditingItemId(it.id);
  };
  const cancelEdit = () => { setEditingItemId(null); setEditDraft(EMPTY_ITEM); };
  const saveEdit = async () => {
    if (!editingItemId) return;
    if (!editDraft.title.trim()) return toast("Title can't be empty.");
    setEditSaving(true);
    try {
      const payload = {
        kind: editDraft.kind,
        item_category: editDraft.item_category,
        title: editDraft.title.trim(),
        notes: editDraft.notes || null,
        price_hint: editDraft.price_hint || null,
        // Idea-only items MUST NOT keep a link — server also strips it, but
        // sending explicit null makes intent obvious in logs.
        link: editDraft.kind === "specific" ? (editDraft.link.trim() || null) : null,
        store_name: editDraft.store_name || null,
      };
      const { data: updated } = await api.patch(`/gift-items/${editingItemId}`, payload);
      // Splice the updated row into local state — no full reload.
      setItems((prev) => prev.map((it) => (it.id === editingItemId ? updated : it)));
      cancelEdit();
      toast("Updated.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setEditSaving(false);
    }
  };

  const refreshItemPreview = async (itemId) => {
    setRefreshBusyId(itemId);
    try {
      const { data: updated } = await api.post(`/gift-items/${itemId}/refresh-preview`);
      setItems((prev) => prev.map((it) => (it.id === itemId ? updated : it)));
      toast("Preview refreshed.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast(typeof d === "string" ? d : "Couldn't refresh the preview.");
    } finally {
      setRefreshBusyId(null);
    }
  };

  const openItemPhotoPicker = (itemId) => {
    setPendingPhotoItemId(itemId);
    // useEffect below fires the click once the input's `key` swap forces a
    // re-render — avoids a race where the previous file selection leaks
    // into the new upload.
    setTimeout(() => itemPhotoInputRef.current?.click(), 0);
  };

  const onPickItemPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const itemId = pendingPhotoItemId;
    setPendingPhotoItemId(null);
    if (!f || !itemId) return;
    if (!f.type.startsWith("image/")) return toast("Please choose an image.");
    if (f.size > 6 * 1024 * 1024) return toast("Please keep it under 6MB.");
    setPhotoBusyId(itemId);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: updated } = await api.post(`/gift-items/${itemId}/photo`, { path: up.path });
      setItems((prev) => prev.map((it) => (it.id === itemId ? updated : it)));
      toast("Your photo added.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't upload the photo.");
    } finally {
      setPhotoBusyId(null);
    }
  };

  const clearItemPhotoOverride = async (itemId) => {
    setPhotoBusyId(itemId);
    try {
      const { data: updated } = await api.post(`/gift-items/${itemId}/photo`, { path: null });
      setItems((prev) => prev.map((it) => (it.id === itemId ? updated : it)));
      toast("Your photo removed. Falling back to the site's preview.");
    } catch {
      toast("Couldn't remove the photo.");
    } finally {
      setPhotoBusyId(null);
    }
  };

  // ---- Recommend endorsement (private to the owner) ----
  // The comment box is expanded lazily — starts empty; opens after Yes/No so
  // it doesn't crowd the row. Kept in a Map keyed by item id so multiple
  // rows can be in different states at once without stepping on each other.
  const [recommendBusyId, setRecommendBusyId] = useState(null);
  const [openCommentId, setOpenCommentId] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");

  const setItemRecommend = async (itemId, status, comment) => {
    setRecommendBusyId(itemId);
    try {
      const { data } = await api.post(`/gift-items/${itemId}/recommend`, {
        status,
        comment: comment ?? undefined,
      });
      setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...data } : it)));
      if (status === null) toast("Recommendation cleared.");
      else if (comment) toast("Recommendation saved.");
      // For Yes without a comment yet, stay silent — the "plant a seed" nudge
      // is the reward.
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't save recommendation.");
    } finally {
      setRecommendBusyId(null);
    }
  };

  // Prefill the Plant flow from this gift item — same sessionStorage
  // handshake the ShopThis "turn into a full seed" button uses.
  const plantSeedFromItem = (it) => {
    try {
      const cover = it.preview_image_override_path || null;
      const prefill = {
        source: "gift-item",
        gift_item_id: it.id,
        title: it.title || "",
        photo_path: cover,
        recommend_hint: it.recommend_comment || "",
        shop_item: it.link
          ? { item_name: it.title, url: it.link, store_name: it.store_name || null, price_hint: it.price_hint || null }
          : null,
      };
      sessionStorage.setItem("willoah.plant.shop_prefill.v1", JSON.stringify(prefill));
      navigate("/plant?from=gift-item");
    } catch {
      toast("Couldn't start the plant flow.");
    }
  };

  const toggleShare = async () => {
    if (!list) return;
    setShareBusy(true);
    try {
      const enable = !list.share_token;
      const { data } = await api.post(`/gift-lists/${id}/share`, { enable });
      setList({ ...list, share_token: data.share_token });
      if (data.share_token) toast("Share link ready.");
      else toast("Sharing turned off.");
    } catch {
      toast("Couldn't update sharing.");
    } finally {
      setShareBusy(false);
    }
  };

  // Share URL uses REACT_APP_PUBLIC_URL when set (production custom domain,
  // e.g. https://willoah.com) so links Kelly sends to friends always land
  // on the branded domain no matter which environment she copied them from.
  // Falls back to the current origin for local development / preview testing.
  const publicOrigin = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
  const shareUrl = list?.share_token
    ? `${publicOrigin}/gl/${list.share_token}`
    : null;

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast("Copy failed — long-press the link instead.");
    }
  };

  const uploadCover = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast("Please pick an image.");
      return;
    }
    setCoverBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.patch(`/gift-lists/${id}`, { cover_photo_path: data.path });
      setList((l) => ({ ...l, cover_photo_path: data.path }));
      toast("Cover updated.");
    } catch {
      toast("Couldn't upload the cover.");
    } finally {
      setCoverBusy(false);
    }
  };

  const clearCover = async () => {
    setCoverBusy(true);
    try {
      await api.patch(`/gift-lists/${id}`, { cover_photo_path: "" });
      setList((l) => ({ ...l, cover_photo_path: null }));
      toast("Cover removed.");
    } catch {
      toast("Couldn't remove the cover.");
    } finally {
      setCoverBusy(false);
    }
  };

  const saveOccasion = async () => {
    setOccasionBusy(true);
    try {
      await api.patch(`/gift-lists/${id}`, {
        occasion_date: occasionEdit || "",
        occasion_recurring: occasionRecurringEdit,
      });
      setList((l) => ({
        ...l,
        occasion_date: occasionEdit || null,
        occasion_recurring: occasionRecurringEdit,
      }));
      toast(occasionEdit ? "Date saved." : "Date cleared.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't save the date.");
    } finally {
      setOccasionBusy(false);
    }
  };

  // ---- List name (title) inline edit ----
  // Opens a text input in place of the H1. Enter or the Save tick commits;
  // Escape or cancel discards. Updates local `list.name` on success so the
  // whole page never has to re-fetch (mobile users complained editing here
  // looked like a full page reload).
  const startEditingListName = () => {
    setListNameDraft(list?.name || "");
    setEditingListName(true);
  };
  const cancelEditingListName = () => {
    setEditingListName(false);
    setListNameDraft("");
  };
  const saveListName = async () => {
    const next = listNameDraft.trim();
    if (!next) {
      toast("List name can't be empty.");
      return;
    }
    // 80-char cap matches the server-side clamp; give the same feedback
    // instead of letting the server silently truncate the user's input.
    if (next.length > 80) {
      toast("Name is a little long — 80 characters max.");
      return;
    }
    if (next === list.name) {
      // No-op edit — just close the editor without hitting the network.
      cancelEditingListName();
      return;
    }
    setListNameSaving(true);
    try {
      await api.patch(`/gift-lists/${id}`, { name: next });
      setList((l) => ({ ...l, name: next }));
      cancelEditingListName();
      toast("Name updated.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't save the new name.");
    } finally {
      setListNameSaving(false);
    }
  };

  // ---- Custom categories ----
  // Owner-scoped, list-scoped. Add creates, remove deletes and any items
  // pointing at that category quietly fall back to "product" server-side.
  const openAddCategory = () => {
    setNewCatOpen(true);
    setNewCatLabel("");
  };
  const cancelAddCategory = () => {
    setNewCatOpen(false);
    setNewCatLabel("");
  };
  const saveCustomCategory = async () => {
    const label = newCatLabel.trim();
    if (!label) {
      toast("Give this category a short name.");
      return;
    }
    setNewCatSaving(true);
    try {
      const { data } = await api.post(`/gift-lists/${id}/custom-categories`, { label });
      // Server returns full array + the added entry. Update local list state
      // so the picker rows re-render instantly (no full-page reload).
      setList((l) => ({ ...l, custom_categories: data.custom_categories }));
      // Auto-select the new category so the user's next add uses it — matches
      // the moment of intent (they added the category to categorize the item
      // they're about to add).
      if (data.added?.slug) {
        setDraft((d) => ({ ...d, item_category: data.added.slug }));
      }
      cancelAddCategory();
      toast("Category added.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't add that category.");
    } finally {
      setNewCatSaving(false);
    }
  };
  const removeCustomCategory = async (slug, label) => {
    if (!window.confirm(`Remove the "${label}" category? Any items using it will move to Product.`)) return;
    try {
      const { data } = await api.delete(`/gift-lists/${id}/custom-categories/${encodeURIComponent(slug)}`);
      setList((l) => ({ ...l, custom_categories: data.custom_categories }));
      // If the current add/edit draft or filter pointed at this slug,
      // reset those pointers so the UI stays consistent.
      if (draft.item_category === slug) setDraft((d) => ({ ...d, item_category: "product" }));
      if (editDraft.item_category === slug) setEditDraft((d) => ({ ...d, item_category: "product" }));
      if (filter === slug) setFilter("all");
      // Locally fall any surfaced items over to "product" so we don't wait
      // for a refetch — the server did the same thing.
      setItems((prev) => prev.map((i) => (i.item_category === slug ? { ...i, item_category: "product" } : i)));
      const reassigned = data.reassigned_item_count || 0;
      toast(reassigned ? `Category removed. ${reassigned} ${reassigned === 1 ? "item" : "items"} moved to Product.` : "Category removed.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't remove that category.");
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-center">
        <p className="text-[#2A2823]/70">Sign in to view your gift lists.</p>
      </main>
    );
  }
  if (loading || !list) {
    return <main className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-[#2A2823]/60 text-sm">Loading…</main>;
  }

  return (
    <main className="max-w-3xl mx-auto px-6 pt-8 pb-24" data-testid="gift-list-detail">
      <Link
        to="/gift-lists"
        className="inline-flex items-center gap-2 text-sm text-[#2A2823]/60 hover:text-[#2A2823] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
        All lists
      </Link>

      <div className="mt-6 flex items-start gap-3">
        <Gift className="w-6 h-6 text-[#B79256] mt-3" strokeWidth={1.75} />
        <div className="min-w-0 flex-1">
          <span className="accent-label text-[#B79256]">gift list</span>
          {editingListName ? (
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="gift-list-title-edit">
              <Input
                autoFocus
                data-testid="gift-list-title-input"
                value={listNameDraft}
                onChange={(e) => setListNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); saveListName(); }
                  if (e.key === "Escape") { e.preventDefault(); cancelEditingListName(); }
                }}
                maxLength={80}
                placeholder="List name"
                className="font-serif-display text-3xl sm:text-4xl h-14 rounded-2xl border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-4 py-2 leading-tight"
              />
              <div className="flex items-center gap-2">
                <Button
                  data-testid="gift-list-title-save"
                  onClick={saveListName}
                  disabled={listNameSaving}
                  className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-10 px-4 shadow-none"
                >
                  <Check className="w-4 h-4 mr-1.5" strokeWidth={2} />
                  {listNameSaving ? "Saving…" : "Save"}
                </Button>
                <button
                  type="button"
                  data-testid="gift-list-title-cancel"
                  onClick={cancelEditingListName}
                  disabled={listNameSaving}
                  className="text-sm text-[#2A2823]/60 hover:text-[#2A2823] transition-colors px-2"
                >
                  cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex items-start gap-2 group">
              <h1
                data-testid="gift-list-title"
                className="font-serif-display text-4xl sm:text-5xl text-[#2A2823] leading-none min-w-0 break-words"
              >
                {list.name}
              </h1>
              <button
                type="button"
                data-testid="gift-list-title-edit-btn"
                onClick={startEditingListName}
                aria-label="Edit list name"
                title="Edit list name"
                className="mt-2 p-2 rounded-full text-[#2A2823]/40 hover:text-[#4E5A3D] hover:bg-[#4E5A3D]/10 transition-colors opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
              >
                <Pencil className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cover photo */}
      <div className="mt-6 relative rounded-3xl overflow-hidden border border-[#B79256]/25 bg-[#B79256]/8" data-testid="cover-panel">
        <div className="aspect-[3/1] w-full relative">
          {list.cover_photo_path ? (
            <img
              data-testid="cover-image"
              src={FILE_URL(list.cover_photo_path)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-[#B79256]/70">
              <Camera className="w-6 h-6 mb-2" strokeWidth={1.5} />
              <span className="text-sm">Add a cover photo so this list feels like a postcard.</span>
            </div>
          )}
        </div>
        <div className="absolute top-3 right-3 flex gap-2">
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="cover-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadCover(f);
              e.target.value = "";
            }}
          />
          <Button
            data-testid="cover-upload-btn"
            onClick={() => coverInputRef.current?.click()}
            disabled={coverBusy}
            className="rounded-full bg-white/90 backdrop-blur text-[#2A2823] hover:bg-white h-9 px-4 border border-[#B79256]/40 shadow-none text-sm"
          >
            <Camera className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} />
            {list.cover_photo_path ? "Change cover" : "Add cover"}
          </Button>
          {list.cover_photo_path && (
            <Button
              data-testid="cover-clear-btn"
              onClick={clearCover}
              disabled={coverBusy}
              className="rounded-full bg-white/90 backdrop-blur text-[#2A2823] hover:bg-white h-9 px-3 border border-[#B79256]/40 shadow-none text-sm"
              title="Remove cover"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Button>
          )}
        </div>
      </div>

      {/* Owner-blind reminder */}
      <div className="mt-6 rounded-2xl border border-[#B79256]/30 bg-[#B79256]/8 px-5 py-4 flex items-start gap-3">
        <Info className="w-4 h-4 text-[#B79256] mt-0.5 shrink-0" strokeWidth={1.75} />
        <p className="text-sm text-[#2A2823]/75">
          Only you can see this page. Even after you share the link, you'll never see which items your
          friends have claimed — every item stays "available" here so the surprise is safe.
        </p>
      </div>

      {!list.occasion_date && (
        <div
          data-testid="just-because-pill"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#4E5A3D]/10 border border-[#4E5A3D]/25 px-3 py-1"
        >
          <Gift className="w-3 h-3 text-[#4E5A3D]" strokeWidth={2} />
          <span className="accent-label text-[10px] text-[#4E5A3D]">just because · no occasion</span>
        </div>
      )}

      {/* Occasion date panel */}
      <div className="mt-6 rounded-2xl border border-[#B79256]/25 bg-white/60 p-5" data-testid="occasion-panel">
        <div className="flex items-center gap-3">
          <CalendarClock className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75} />
          <span className="font-serif text-lg text-[#2A2823]">Occasion date (optional)</span>
        </div>
        <p className="text-xs text-[#2A2823]/60 mt-1">
          When the day is close, we'll gently nudge you on the dashboard so you can top up your list.
          {!list.occasion_date && (
            <span className="ml-1 italic text-[#8B6E3F]">Leave blank for a "just because" list — small, no-pressure wants with no specific event.</span>
          )}
        </p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <Input
            type="date"
            data-testid="occasion-input"
            value={occasionEdit || ""}
            onChange={(e) => setOccasionEdit(e.target.value)}
            className="h-11 rounded-full border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5 max-w-[240px]"
          />
          <Button
            data-testid="occasion-save"
            onClick={saveOccasion}
            disabled={occasionBusy}
            className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-5 shadow-none"
          >
            Save
          </Button>
          {list.occasion_date && (
            <button
              type="button"
              data-testid="occasion-clear"
              onClick={() => { setOccasionEdit(""); saveOccasion(); }}
              className="text-sm text-[#2A2823]/50 hover:text-[#2A2823] transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#2A2823]/75 cursor-pointer">
          <input
            type="checkbox"
            data-testid="occasion-recurring"
            checked={occasionRecurringEdit}
            onChange={(e) => setOccasionRecurringEdit(e.target.checked)}
            className="accent-[#4E5A3D]"
          />
          <span>Recurs every year (like a birthday)</span>
        </label>
      </div>

      {/* Share panel */}
      <div className="mt-6 rounded-2xl border border-[#B79256]/25 bg-white/60 p-5" data-testid="share-panel">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Share2 className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75} />
            <span className="font-serif text-lg text-[#2A2823]">Share this list</span>
          </div>
          <button
            type="button"
            data-testid="toggle-share"
            onClick={toggleShare}
            disabled={shareBusy}
            className={`text-xs accent-label transition-colors ${
              list.share_token
                ? "text-red-800 hover:text-red-900"
                : "text-[#4E5A3D] hover:text-[#3E4830]"
            }`}
          >
            {list.share_token ? "Turn off sharing" : "Turn on sharing"}
          </button>
        </div>
        {list.share_token && shareUrl && (
          <div className="mt-3 flex items-center gap-2">
            <code
              data-testid="share-url"
              className="flex-1 truncate text-sm bg-[#FBF8F1] rounded-full px-4 py-2 border border-[#B79256]/30 text-[#2A2823]/80"
            >
              {shareUrl}
            </code>
            <Button
              type="button"
              data-testid="copy-share"
              onClick={copyShare}
              className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 shadow-none"
            >
              {copied ? <Check className="w-4 h-4" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
            </Button>
          </div>
        )}
      </div>

      {/* Add item form */}
      <form onSubmit={addItem} className="mt-10 rounded-2xl border border-[#B79256]/25 bg-white/60 p-5" data-testid="add-item-form">
        <div className="accent-label text-[#4E5A3D]">Add an item</div>

        {/* Category picker — the "what is this thing?" chip row. Sits above
            the kind toggle because it changes every downstream placeholder
            and lets the picker be primary. */}
        <div className="mt-3">
          <div className="accent-label text-[10px] text-[#B79256] mb-2">Kind of gift</div>
          <div className="flex flex-wrap gap-2" data-testid="item-category-picker">
            {allCategories.map((c) => {
              const Icon = c.icon;
              const active = draft.item_category === c.id;
              return (
                <span
                  key={c.id}
                  className={`inline-flex items-center rounded-full border transition-colors ${
                    active
                      ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
                      : "bg-transparent text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/10"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`item-category-${c.id}`}
                    onClick={() => setDraft((d) => ({ ...d, item_category: c.id }))}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
                    title={c.hint}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                    {c.label}
                  </button>
                  {c.is_custom && (
                    <button
                      type="button"
                      data-testid={`item-category-remove-${c.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustomCategory(c.id, c.label);
                      }}
                      title={`Remove ${c.label}`}
                      aria-label={`Remove ${c.label}`}
                      className={`pl-0.5 pr-2 py-1.5 rounded-r-full text-xs ${
                        active ? "text-[#FBF8F1]/80 hover:text-[#FBF8F1]" : "text-[#2A2823]/40 hover:text-red-800"
                      }`}
                    >
                      <XIcon className="w-3 h-3" strokeWidth={2} />
                    </button>
                  )}
                </span>
              );
            })}
            {!newCatOpen && customCategories.length < 8 && (
              <button
                type="button"
                data-testid="item-category-add-btn"
                onClick={openAddCategory}
                className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#B79256]/60 px-3 py-1.5 text-xs text-[#8B6E3F] hover:bg-[#B79256]/10 transition-colors"
                title="Add your own category to this list"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                Add category
              </button>
            )}
            {newCatOpen && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#4E5A3D] bg-[#FBF8F1] pl-3 pr-1 py-0.5" data-testid="new-category-editor">
                <Input
                  autoFocus
                  data-testid="new-category-input"
                  value={newCatLabel}
                  onChange={(e) => setNewCatLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); saveCustomCategory(); }
                    if (e.key === "Escape") { e.preventDefault(); cancelAddCategory(); }
                  }}
                  maxLength={24}
                  placeholder="e.g., Coffee, Reading, Home"
                  className="h-7 w-40 sm:w-48 border-0 shadow-none bg-transparent text-xs p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="button"
                  data-testid="new-category-save"
                  onClick={saveCustomCategory}
                  disabled={newCatSaving}
                  className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] px-2.5 py-1 text-[11px] hover:bg-[#3E4830] disabled:opacity-60"
                >
                  {newCatSaving ? "…" : "Save"}
                </button>
                <button
                  type="button"
                  data-testid="new-category-cancel"
                  onClick={cancelAddCategory}
                  disabled={newCatSaving}
                  className="text-[#2A2823]/50 hover:text-[#2A2823] px-1.5 text-xs"
                  aria-label="Cancel"
                >
                  <XIcon className="w-3 h-3" strokeWidth={2} />
                </button>
              </span>
            )}
          </div>
          <p className="text-[11px] text-[#2A2823]/55 mt-2 italic">
            {allCategoriesById[draft.item_category]?.hint}
          </p>
        </div>

        <div className="mt-4 inline-flex rounded-full border border-[#B79256]/35 p-1 bg-[#FBF8F1]">
          <button
            type="button"
            data-testid="kind-specific"
            onClick={() => setDraft((d) => ({ ...d, kind: "specific" }))}
            className={`accent-label px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
              draft.kind === "specific"
                ? "bg-[#4E5A3D] text-[#FBF8F1]"
                : "text-[#2A2823]/70 hover:text-[#2A2823]"
            }`}
          >
            <LinkIcon className="w-3.5 h-3.5" strokeWidth={2} />
            Buy this exact one
          </button>
          <button
            type="button"
            data-testid="kind-idea"
            onClick={() => setDraft((d) => ({ ...d, kind: "idea_only" }))}
            className={`accent-label px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
              draft.kind === "idea_only"
                ? "bg-[#B79256] text-[#FBF8F1]"
                : "text-[#2A2823]/70 hover:text-[#2A2823]"
            }`}
          >
            <Lightbulb className="w-3.5 h-3.5" strokeWidth={2} />
            Idea only
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {(() => {
            const cat = allCategoriesById[draft.item_category] || allCategoriesById.product;
            const isSpecific = draft.kind === "specific";
            return (
              <>
                <Input
                  data-testid="item-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder={isSpecific ? cat.specific_ph : cat.idea_ph}
                  className="h-11 rounded-full border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5"
                  maxLength={200}
                />
                {isSpecific && (
                  <>
                    <Input
                      data-testid="item-link"
                      value={draft.link}
                      onChange={(e) => setDraft({ ...draft, link: e.target.value })}
                      placeholder={cat.link_ph}
                      className="h-11 rounded-full border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5"
                    />
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        data-testid="item-store"
                        value={draft.store_name}
                        onChange={(e) => setDraft({ ...draft, store_name: e.target.value })}
                        placeholder={`${cat.store_label} (optional)`}
                        className="h-11 rounded-full border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5"
                        maxLength={80}
                      />
                      <Input
                        data-testid="item-price"
                        value={draft.price_hint}
                        onChange={(e) => setDraft({ ...draft, price_hint: e.target.value })}
                        placeholder="Price hint ($, £, etc.) — optional"
                        className="h-11 rounded-full border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5 sm:max-w-[220px]"
                        maxLength={40}
                      />
                    </div>
                  </>
                )}
                {!isSpecific && (
                  <Input
                    data-testid="item-price-idea"
                    value={draft.price_hint}
                    onChange={(e) => setDraft({ ...draft, price_hint: e.target.value })}
                    placeholder="Price hint (optional) — helps friends aim for the right budget"
                    className="h-11 rounded-full border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5 sm:max-w-[320px]"
                    maxLength={40}
                  />
                )}
                <Textarea
                  data-testid="item-notes"
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  placeholder={isSpecific ? "Anything a friend should know (size, color, why you want it)…" : "A bit of context so friends know what would land…"}
                  className="rounded-2xl border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5"
                  maxLength={600}
                />
              </>
            );
          })()}
          <div className="flex justify-end">
            <Button
              type="submit"
              data-testid="add-item-submit"
              disabled={saving || !draft.title.trim()}
              className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-10 px-5 shadow-none"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Add to list
            </Button>
          </div>
        </div>
      </form>

      {/* Items */}
      <div className="mt-10" data-testid="gift-items-section">
        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="item-filter-row">
            {(() => {
              const counts = { all: items.length };
              for (const it of items) {
                const c = it.item_category || "product";
                counts[c] = (counts[c] || 0) + 1;
              }
              const chips = [
                { id: "all", label: "All", icon: null },
                ...allCategories,
              ];
              return chips.map((c) => {
                const Icon = c.icon;
                const n = counts[c.id] || 0;
                const active = filter === c.id;
                // Hide zero-count filters so an empty "Handmade" chip doesn't
                // sit next to a full "Product" chip — keeps the row tidy.
                if (c.id !== "all" && n === 0) return null;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`item-filter-${c.id}`}
                    onClick={() => setFilter(c.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                      active
                        ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
                        : "bg-transparent text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/10"
                    }`}
                  >
                    {Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
                    {c.label}
                    <span className={active ? "text-[#FBF8F1]/80" : "text-[#2A2823]/50"}>· {n}</span>
                  </button>
                );
              });
            })()}
          </div>
        )}

        <div className="space-y-3" data-testid="gift-items">
          {items.length === 0 && (
            <p className="text-[#2A2823]/60 text-sm">
              No items yet. Add the first one above — pick a specific find or leave it as an idea.
            </p>
          )}
          {items
            .filter((it) => filter === "all" || (it.item_category || "product") === filter)
            .map((it) => {
              const isEditing = editingItemId === it.id;
              if (isEditing) {
                const cat = allCategoriesById[editDraft.item_category] || allCategoriesById.product;
                const isSpecific = editDraft.kind === "specific";
                return (
                  <div
                    key={it.id}
                    data-testid={`item-edit-${it.id}`}
                    className="rounded-2xl border border-[#4E5A3D]/40 bg-[#FBF8F1] p-4 space-y-3"
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="accent-label text-[#4E5A3D]">Edit item</div>
                      <button
                        type="button"
                        data-testid={`item-edit-cancel-${it.id}`}
                        onClick={cancelEdit}
                        className="text-xs text-[#2A2823]/60 hover:text-[#2A2823]"
                      >
                        <XIcon className="w-3.5 h-3.5 inline mr-1" strokeWidth={1.75}/>cancel
                      </button>
                    </div>
                    <div>
                      <div className="accent-label text-[10px] text-[#B79256] mb-1.5">Kind of gift</div>
                      <div className="flex flex-wrap gap-1.5">
                        {allCategories.map((c) => {
                          const Icon = c.icon;
                          const active = editDraft.item_category === c.id;
                          return (
                            <button
                              type="button"
                              key={c.id}
                              data-testid={`edit-item-category-${c.id}`}
                              onClick={() => setEditDraft((d) => ({ ...d, item_category: c.id }))}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                                active
                                  ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
                                  : "bg-transparent text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/10"
                              }`}
                            >
                              <Icon className="w-3 h-3" strokeWidth={2}/>{c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="inline-flex rounded-full border border-[#B79256]/35 p-1 bg-white">
                      <button
                        type="button"
                        data-testid={`edit-kind-specific-${it.id}`}
                        onClick={() => setEditDraft((d) => ({ ...d, kind: "specific" }))}
                        className={`text-xs rounded-full px-3 py-1 ${isSpecific ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/70"}`}
                      >
                        Specific
                      </button>
                      <button
                        type="button"
                        data-testid={`edit-kind-idea-${it.id}`}
                        onClick={() => setEditDraft((d) => ({ ...d, kind: "idea_only" }))}
                        className={`text-xs rounded-full px-3 py-1 ${!isSpecific ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/70"}`}
                      >
                        Just an idea
                      </button>
                    </div>
                    <Input
                      data-testid={`edit-item-title-${it.id}`}
                      value={editDraft.title}
                      onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                      placeholder={isSpecific ? cat.specific_ph : cat.idea_ph}
                      maxLength={200}
                      className="h-10 rounded-full border-[#B79256]/40 bg-white px-4"
                    />
                    {isSpecific && (
                      <>
                        <Input
                          data-testid={`edit-item-link-${it.id}`}
                          value={editDraft.link}
                          onChange={(e) => setEditDraft({ ...editDraft, link: e.target.value })}
                          placeholder={cat.link_ph}
                          className="h-10 rounded-full border-[#B79256]/40 bg-white px-4"
                        />
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Input
                            data-testid={`edit-item-store-${it.id}`}
                            value={editDraft.store_name}
                            onChange={(e) => setEditDraft({ ...editDraft, store_name: e.target.value })}
                            placeholder={`${cat.store_label} (optional)`}
                            className="h-10 rounded-full border-[#B79256]/40 bg-white px-4"
                            maxLength={80}
                          />
                          <Input
                            data-testid={`edit-item-price-${it.id}`}
                            value={editDraft.price_hint}
                            onChange={(e) => setEditDraft({ ...editDraft, price_hint: e.target.value })}
                            placeholder="Price hint (optional)"
                            className="h-10 rounded-full border-[#B79256]/40 bg-white px-4 sm:max-w-[220px]"
                            maxLength={40}
                          />
                        </div>
                      </>
                    )}
                    {!isSpecific && (
                      <Input
                        data-testid={`edit-item-price-idea-${it.id}`}
                        value={editDraft.price_hint}
                        onChange={(e) => setEditDraft({ ...editDraft, price_hint: e.target.value })}
                        placeholder="Price hint (optional)"
                        className="h-10 rounded-full border-[#B79256]/40 bg-white px-4 sm:max-w-[320px]"
                        maxLength={40}
                      />
                    )}
                    <Textarea
                      data-testid={`edit-item-notes-${it.id}`}
                      value={editDraft.notes}
                      onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                      placeholder="Anything a friend should know…"
                      className="rounded-2xl border-[#B79256]/40 bg-white px-4"
                      maxLength={600}
                    />
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        data-testid={`edit-item-save-${it.id}`}
                        onClick={saveEdit}
                        disabled={editSaving}
                        className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-10 px-5 shadow-none"
                      >
                        <Check className="w-4 h-4 mr-2" strokeWidth={1.75}/>
                        {editSaving ? "Saving…" : "Save changes"}
                      </Button>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={it.id}
                  data-testid={`item-row-${it.id}`}
                  className="rounded-2xl border border-[#B79256]/25 bg-white/60 p-4 flex items-start gap-4"
                >
                  {it.kind === "specific" ? (
                    <LinkPreviewThumb
                      src={it.preview_image_url}
                      overridePath={it.preview_image_override_path}
                      alt={it.title}
                      size="md"
                      testId={`gift-item-preview-${it.id}`}
                    />
                  ) : it.preview_image_override_path ? (
                    <LinkPreviewThumb
                      src={null}
                      overridePath={it.preview_image_override_path}
                      alt={it.title}
                      size="md"
                      testId={`gift-item-idea-photo-${it.id}`}
                    />
                  ) : (
                    <span
                      className="shrink-0 w-16 h-16 rounded-lg bg-[#B79256]/15 text-[#B79256] flex items-center justify-center"
                      title="Idea only"
                      aria-hidden="true"
                    >
                      <Lightbulb className="w-5 h-5" strokeWidth={1.75} />
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-serif text-lg text-[#2A2823]">{it.title}</span>
                      {(() => {
                        const cat = allCategoriesById[it.item_category] || allCategoriesById.product;
                        const Icon = cat.icon;
                        return (
                          <span
                            data-testid={`gift-item-category-${it.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#B79256]/10 border border-[#B79256]/30 text-[#8B6E3F] px-2 py-0.5 text-[10px]"
                            title={cat.hint}
                          >
                            <Icon className="w-2.5 h-2.5" strokeWidth={2} />
                            {cat.label}
                          </span>
                        );
                      })()}
                      {it.price_hint && (
                        <span className="text-xs text-[#2A2823]/50">{it.price_hint}</span>
                      )}
                    </div>
                    {it.store_name && it.kind === "specific" && (
                      <div
                        data-testid={`gift-item-store-${it.id}`}
                        className="accent-label text-[10px] text-[#2A2823]/60 mt-0.5"
                      >
                        {it.store_name}
                      </div>
                    )}
                    {it.notes && (
                      <p className="text-sm text-[#2A2823]/70 mt-1 whitespace-pre-wrap">{it.notes}</p>
                    )}
                    {it.link && it.kind === "specific" && (
                      <a
                        href={it.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] hover:underline"
                      >
                        <LinkIcon className="w-3 h-3" strokeWidth={1.75} />
                        Open link
                      </a>
                    )}
                    {/* Preview controls — available on every item, not just
                        specific-kind with links. Idea-only items also
                        benefit from a photo (e.g. "any local pottery
                        shop" is way more evocative with a real photo). */}
                    {it.kind === "specific" && it.link && (
                      <>
                        {!it.preview_image_url && !it.preview_image_override_path && (
                          <button
                            type="button"
                            data-testid={`gift-item-upload-photo-empty-${it.id}`}
                            onClick={() => openItemPhotoPicker(it.id)}
                            disabled={photoBusyId === it.id}
                            className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#B79256]/70 bg-[#B79256]/15 hover:bg-[#B79256]/25 text-[#8B6E3F] hover:text-[#6b5430] py-2.5 px-3 transition-colors disabled:opacity-60"
                            title="Some ticket/venue sites block preview scraping — add your own photo so friends see what this is"
                          >
                            <ImagePlus className="w-4 h-4" strokeWidth={2} />
                            <span className="text-xs font-medium">
                              {photoBusyId === it.id ? "Uploading…" : "No preview from the site — add your own photo"}
                            </span>
                          </button>
                        )}
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                          {it.preview_image_override_path ? (
                            <button
                              type="button"
                              data-testid={`gift-item-clear-photo-${it.id}`}
                              onClick={() => clearItemPhotoOverride(it.id)}
                              disabled={photoBusyId === it.id}
                              className="inline-flex items-center gap-1 text-[#B24A2A] hover:text-[#963e22]"
                            >
                              <XIcon className="w-3 h-3" strokeWidth={2} /> remove my photo
                            </button>
                          ) : (
                            <>
                              {it.preview_image_url && (
                                <button
                                  type="button"
                                  data-testid={`gift-item-upload-photo-${it.id}`}
                                  onClick={() => openItemPhotoPicker(it.id)}
                                  disabled={photoBusyId === it.id}
                                  className="inline-flex items-center gap-1 text-[#4E5A3D] hover:text-[#3E4830]"
                                  title="Use your own photo instead of the site's preview"
                                >
                                  <ImagePlus className="w-3 h-3" strokeWidth={2} />
                                  {photoBusyId === it.id ? "uploading…" : "use my own photo"}
                                </button>
                              )}
                              <button
                                type="button"
                                data-testid={`gift-item-refresh-${it.id}`}
                                onClick={() => refreshItemPreview(it.id)}
                                disabled={refreshBusyId === it.id}
                                className="inline-flex items-center gap-1 text-[#2A2823]/60 hover:text-[#4E5A3D]"
                                title="Retry fetching the site's photo"
                              >
                                <RefreshCcw className={`w-3 h-3 ${refreshBusyId === it.id ? "animate-spin" : ""}`} strokeWidth={2} />
                                {refreshBusyId === it.id ? "retrying…" : "retry preview"}
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    {/* Idea-only or linkless items still deserve a photo —
                        e.g. "any local pottery class" reads much warmer with
                        a real photo Kelly picks herself. Simple photo add /
                        remove, no retry (there's no site preview to fetch). */}
                    {(it.kind !== "specific" || !it.link) && (
                      <>
                        {!it.preview_image_override_path ? (
                          <button
                            type="button"
                            data-testid={`gift-item-add-idea-photo-${it.id}`}
                            onClick={() => openItemPhotoPicker(it.id)}
                            disabled={photoBusyId === it.id}
                            className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#B79256]/70 bg-[#B79256]/15 hover:bg-[#B79256]/25 text-[#8B6E3F] hover:text-[#6b5430] py-2.5 px-3 transition-colors disabled:opacity-60"
                            title="Add a photo so this item feels tangible — even without a link"
                          >
                            <ImagePlus className="w-4 h-4" strokeWidth={2} />
                            <span className="text-xs font-medium">
                              {photoBusyId === it.id ? "Uploading…" : "Add a photo for this item"}
                            </span>
                          </button>
                        ) : (
                          <div className="mt-2">
                            <button
                              type="button"
                              data-testid={`gift-item-clear-idea-photo-${it.id}`}
                              onClick={() => clearItemPhotoOverride(it.id)}
                              disabled={photoBusyId === it.id}
                              className="inline-flex items-center gap-1 text-[11px] text-[#B24A2A] hover:text-[#963e22]"
                            >
                              <XIcon className="w-3 h-3" strokeWidth={2} /> remove my photo
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    {/* Private "would you recommend?" endorsement — visible
                        ONLY to the list owner. Never appears on shared
                        views. Prompts for a one-line comment after Yes/No,
                        then nudges toward planting a public seed. */}
                    <div
                      data-testid={`gift-item-recommend-${it.id}`}
                      className="mt-3 rounded-xl border border-[#B79256]/25 bg-[#FBF8F1] px-3 py-2.5"
                    >
                      {!it.recommend_status ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-[#2A2823]/65">
                            Once you've tried it — would you recommend this?
                          </span>
                          <button
                            type="button"
                            data-testid={`recommend-yes-${it.id}`}
                            disabled={recommendBusyId === it.id}
                            onClick={() => { setItemRecommend(it.id, "yes"); setOpenCommentId(it.id); setCommentDraft(""); }}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#4E5A3D]/40 bg-white hover:bg-[#4E5A3D]/10 text-[#4E5A3D] px-3 py-1 text-xs transition-colors disabled:opacity-60"
                          >
                            <ThumbsUp className="w-3 h-3" strokeWidth={2} /> Yes
                          </button>
                          <button
                            type="button"
                            data-testid={`recommend-no-${it.id}`}
                            disabled={recommendBusyId === it.id}
                            onClick={() => { setItemRecommend(it.id, "no"); setOpenCommentId(it.id); setCommentDraft(""); }}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#B24A2A]/40 bg-white hover:bg-[#B24A2A]/10 text-[#B24A2A] px-3 py-1 text-xs transition-colors disabled:opacity-60"
                          >
                            <ThumbsDown className="w-3 h-3" strokeWidth={2} /> No
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              data-testid={`recommend-badge-${it.id}`}
                              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border ${
                                it.recommend_status === "yes"
                                  ? "bg-[#4E5A3D]/12 border-[#4E5A3D]/35 text-[#4E5A3D]"
                                  : "bg-[#B24A2A]/10 border-[#B24A2A]/35 text-[#B24A2A]"
                              }`}
                            >
                              {it.recommend_status === "yes" ? (
                                <><ThumbsUp className="w-3 h-3" strokeWidth={2}/> You'd recommend it</>
                              ) : (
                                <><ThumbsDown className="w-3 h-3" strokeWidth={2}/> You wouldn't</>
                              )}
                            </span>
                            <span className="text-[10px] text-[#2A2823]/45">private to you</span>
                            <button
                              type="button"
                              data-testid={`recommend-clear-${it.id}`}
                              disabled={recommendBusyId === it.id}
                              onClick={() => { setItemRecommend(it.id, null); setOpenCommentId(null); }}
                              className="ml-auto text-[10px] text-[#2A2823]/50 hover:text-[#2A2823]/85 underline underline-offset-2"
                            >
                              clear
                            </button>
                          </div>
                          {it.recommend_comment && openCommentId !== it.id && (
                            <div className="flex items-start gap-1.5 text-xs text-[#2A2823]/70 italic">
                              <MessageSquare className="w-3 h-3 mt-0.5 text-[#B79256]" strokeWidth={2} />
                              <span>"{it.recommend_comment}"</span>
                              <button
                                type="button"
                                data-testid={`recommend-edit-comment-${it.id}`}
                                onClick={() => { setOpenCommentId(it.id); setCommentDraft(it.recommend_comment || ""); }}
                                className="text-[10px] not-italic underline underline-offset-2 text-[#2A2823]/50 hover:text-[#4E5A3D] ml-1"
                              >
                                edit
                              </button>
                            </div>
                          )}
                          {openCommentId === it.id && (
                            <div className="flex items-start gap-2">
                              <Input
                                data-testid={`recommend-comment-input-${it.id}`}
                                value={commentDraft}
                                onChange={(e) => setCommentDraft(e.target.value)}
                                placeholder="One line — what's the honest thing?"
                                maxLength={280}
                                className="rounded-full border-[#B79256]/40 bg-white h-9 text-sm flex-1"
                              />
                              <Button
                                type="button"
                                data-testid={`recommend-comment-save-${it.id}`}
                                disabled={recommendBusyId === it.id}
                                onClick={async () => {
                                  await setItemRecommend(it.id, it.recommend_status, commentDraft.trim());
                                  setOpenCommentId(null);
                                }}
                                className="rounded-full h-9 px-4 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none text-xs"
                              >
                                Save
                              </Button>
                              {(it.recommend_comment || commentDraft) && (
                                <button
                                  type="button"
                                  data-testid={`recommend-comment-cancel-${it.id}`}
                                  onClick={() => setOpenCommentId(null)}
                                  className="text-xs text-[#2A2823]/50 hover:text-[#2A2823] px-1"
                                >
                                  cancel
                                </button>
                              )}
                            </div>
                          )}
                          {/* Optional next step — plant a public seed. Only
                              nudged for Yes responses (a "No" isn't really
                              something people want to publicly review). */}
                          {it.recommend_status === "yes" && (
                            it.planted_idea_id ? (
                              <Link
                                to={`/idea/${it.planted_idea_id}`}
                                data-testid={`recommend-open-seed-${it.id}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#4E5A3D]/35 bg-white hover:bg-[#4E5A3D]/8 text-[#4E5A3D] px-3 py-1 text-xs transition-colors"
                              >
                                <Sprout className="w-3 h-3" strokeWidth={2} /> Open your seed →
                              </Link>
                            ) : (
                              <button
                                type="button"
                                data-testid={`recommend-plant-seed-${it.id}`}
                                onClick={() => plantSeedFromItem(it)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#4E5A3D]/35 bg-white hover:bg-[#4E5A3D]/8 text-[#4E5A3D] px-3 py-1 text-xs transition-colors"
                                title="Optional — share a fuller review. Only the seed becomes public; this list stays private."
                              >
                                <Sprout className="w-3 h-3" strokeWidth={2} /> Want to share more? Plant a seed
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      data-testid={`item-edit-btn-${it.id}`}
                      onClick={() => startEditingItem(it)}
                      className="p-2 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#4E5A3D]/10 transition-colors"
                      aria-label="Edit item"
                      title="Edit this item"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      data-testid={`item-delete-btn-${it.id}`}
                      onClick={() => deleteItem(it.id)}
                      className="p-2 rounded-full text-[#2A2823]/50 hover:text-red-800 hover:bg-red-800/10 transition-colors"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Shared file input for author-supplied item photos. Lives at the
          bottom of the page so a burst of clicks on different item rows
          all target the same DOM input, avoiding re-mounting an input
          per row. `key` swap on the pending item forces a fresh
          <input> so a previous file selection can't leak into a new
          upload attempt. */}
      <input
        key={pendingPhotoItemId || "idle"}
        ref={itemPhotoInputRef}
        data-testid="gift-item-photo-input"
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPickItemPhoto}
      />
    </main>
  );
}
